import React, { Component } from 'react';
import {
  bool, func, shape, string
} from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { surveyOptionsType } from '../../constants/types';
import {
  addErrorMessageAction,
  addSurveyScheduleAction,
  clearSurveyAction,
  getSurveyMessagesAction,
  initSurveyParticipantsAction,
  setSurveyParticipantsAction,
  updateSurveyMessageAction,
  updateSurveyOptionsAction
} from '../../actions';
import BackButton from '../BackButton';
import WithValidation from '../WithValidation';
import SurveyParticipantsEditor from '../SurveyParticipantsEditor';
import SurveyScheduling from '../SurveyScheduling';
import EmailMessageInput from '../EmailMessageInput';
import { reopenSurveyPageValidator } from '../../validators/surveys';
import { API_DATE_FORMAT, routes, surveyFieldIds } from '../../constants';
import { getCourseUnitRealisations, getSurveyInfo, getSurveyParticipants } from '../../api';
import sharedStyles from '../../styles/shared';
import styles from './reopenSurveyView.module.css';
import withRouter from '../WithRouter';
import { withLang, langType } from '../../i18n';
import { getSubRouteForSurveyType, setDocumentTitle } from '../../util';

class ReopenSurveyView extends Component {
  constructor(props) {
    super(props);
    this.submit = this.submit.bind(this);
    this.afterSubmit = this.afterSubmit.bind(this);
    this.onSendStartNotificationChange = this.onSendStartNotificationChange.bind(this);

    this.state = {
      surveyInfo: null,
      sendStartNotification: true
    };
  }

  async componentDidMount() {
    const { surveyId } = this.props.params;
    this.props.dispatchGetSurveyMessages(surveyId);

    try {
      const [survey, participants] = await Promise.all([
        getSurveyInfo(surveyId),
        getSurveyParticipants(surveyId)
      ]);

      const courses = survey.courseRealisationIds?.length
        ? await getCourseUnitRealisations(survey.courseRealisationIds, surveyId)
        : [];

      this.props.dispatchInitParticipants({ participants, courses, onlyManuallyAdded: true });
      this.props.dispatchUpdate({ surveyType: survey.type });

      this.setState({ surveyInfo: survey });
    } catch (err) {
      this.props.dispatchAddErrorMessage(err);
    }
  }

  componentWillUnmount() {
    this.props.dispatchClear();
  }

  onSendStartNotificationChange(event) {
    this.setState({ sendStartNotification: !event.target.checked });
  }

  submit(event) {
    event.preventDefault();
    const { surveyId } = this.props.params;
    this.props.dispatchUpdateSurveyMessage(surveyId, {
      invitationEmailTitle: this.props.surveyOptions.invitationEmailTitle,
      invitationEmailContent: this.props.surveyOptions.invitationEmailContent,
      endingEmailTitle: this.props.surveyOptions.endingEmailTitle,
      endingEmailContent: this.props.surveyOptions.endingEmailContent
    }).then(() => this.props.dispatchAddSurveySchedule(surveyId,
      {
        startTime: this.props.surveyOptions.startDate,
        endTime: this.props.surveyOptions.endDate,
        sendStartNotification: this.state.sendStartNotification
      }))
      .then(() => {
        this.props.dispatchAddParticipants(surveyId, {
          participants: this.props.surveyOptions.participants,
          courseRealisationIds: this.props.surveyOptions.courses?.map((course) => course.id) || []
        });
      })
      .then(this.afterSubmit);
  }

  afterSubmit() {
    this.props.dispatchClear();
    // HACK:This is a hack-y fix, for some reason the unit test for this can't/won't pass the type at all to this component
    this.props.navigate((getSubRouteForSurveyType(this.state.surveyInfo.type) || routes.surveys.hulSurveys).index);
  }

  isFormValid() {
    if (!this.state.sendStartNotification) {
      return this.props.participantsIsValid
        && ![
          surveyFieldIds.ENDING_TITLE,
          surveyFieldIds.ENDING_CONTENT,
          surveyFieldIds.START_DATE,
          surveyFieldIds.END_DATE
        ].some(this.props.hasInvalidField);
    }

    return this.props.isValid && this.props.participantsIsValid;
  }

  render() {
    const { surveyInfo } = this.state;
    const { surveyOptions, params: { surveyId }, translate: t } = this.props;

    const initialStartDate = surveyOptions.startDate
      ? surveyOptions.startDate : moment().format(API_DATE_FORMAT);

    setDocumentTitle(t('manageSurveys.reopen.title'));

    return surveyInfo ? (
      <div className="page-container">
        <BackButton />
        <form>
          <div>
            <h2 className="panel__title">{t('manageSurveys.reopen.title')}</h2>
            <p>{t('manageSurveys.reopen.description')}</p>
            <h2 className="panel__title">{t('manageSurveys.reopen.newParticipants.title')}</h2>
            <p>{t('manageSurveys.reopen.newParticipants.description')}</p>
          </div>
          <div>
            <SurveyParticipantsEditor
              surveyId={surveyId}
              surveyType={surveyOptions.surveyType}
              showAsRequired
            />
          </div>
          <div>
            <h2 className={`panel__title ${styles.schedulingTitle}`}>{t('startSurvey.scheduling')}</h2>
            <SurveyScheduling
              initialStartDate={initialStartDate}
              showProblems
            />
          </div>
          <div>
            <h2 className="panel__title">{t('startSurvey.email.invitationTitle')}</h2>
            <div className="form__item">
              <input
                type="checkbox"
                id="send-start-notification"
                checked={!this.state.sendStartNotification}
                onChange={this.onSendStartNotificationChange}
              />
              <label htmlFor="send-start-notification">
                {t('manageSurveys.reopen.doNotSendStartNotification')}
              </label>
            </div>
            {this.state.sendStartNotification && (
            <EmailMessageInput
              messageTitleName={surveyFieldIds.INVITATION_TITLE}
              messageContentName={surveyFieldIds.INVITATION_CONTENT}
              hasInvalidField={this.props.hasInvalidField}
              dataCyPrefix="survey-invitation-email"
            />
            )}
          </div>
          <div>
            <h2 className="panel__title">{t('startSurvey.email.endingTitle')}</h2>
            <EmailMessageInput
              messageTitleName={surveyFieldIds.ENDING_TITLE}
              messageContentName={surveyFieldIds.ENDING_CONTENT}
              hasInvalidField={this.props.hasInvalidField}
              dataCyPrefix="survey-ending-email"
            />
          </div>
          <button
            type="submit"
            disabled={!this.isFormValid()}
            className={`${sharedStyles.submitButton} button`}
            onClick={this.submit}
            data-cy="reopen-survey-submit-button"
          >
            {t('common.submit')}
          </button>
        </form>
      </div>
    ) : null;
  }
}

ReopenSurveyView.propTypes = {
  params: shape({
    surveyId: string
  }).isRequired,
  surveyOptions: surveyOptionsType.isRequired,
  dispatchGetSurveyMessages: func.isRequired,
  dispatchUpdateSurveyMessage: func.isRequired,
  dispatchAddSurveySchedule: func.isRequired,
  dispatchAddParticipants: func.isRequired,
  dispatchAddErrorMessage: func.isRequired,
  dispatchInitParticipants: func.isRequired,
  dispatchClear: func.isRequired,
  dispatchUpdate: func.isRequired,
  isValid: bool.isRequired,
  hasInvalidField: func.isRequired,
  navigate: func.isRequired,
  participantsIsValid: bool.isRequired,
  translate: langType.translate.isRequired
};

const mapStateToProps = ({ survey }) => ({
  surveyOptions: survey.surveyOptions,
  participantsIsValid: survey.surveyOptions.participants.filter((p) => p.error).length === 0
});

const mapDispatchToProps = (dispatch) => ({

  dispatchAddSurveySchedule: (id, data) =>
    dispatch(addSurveyScheduleAction(id, data)),

  dispatchUpdateSurveyMessage: (id, data) =>
    dispatch(updateSurveyMessageAction(id, data)),

  dispatchAddParticipants: (id, data) =>
    dispatch(setSurveyParticipantsAction(id, data)),

  dispatchGetSurveyMessages: (surveyId) =>
    dispatch(getSurveyMessagesAction(surveyId)),

  dispatchClear: () =>
    dispatch(clearSurveyAction()),

  dispatchAddErrorMessage: (err) =>
    dispatch(addErrorMessageAction(err)),

  dispatchUpdate: (surveyOptions) =>
    dispatch(updateSurveyOptionsAction(surveyOptions)),

  dispatchInitParticipants: (participants) =>
    dispatch(initSurveyParticipantsAction(participants))
});

export default connect(mapStateToProps,
  mapDispatchToProps)(withRouter(withLang(WithValidation(ReopenSurveyView, reopenSurveyPageValidator))));
